import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useDistributionStore} from '../store/distributionStore';
import {useDistributionUIStore} from '../store/distributionUIStore';
import {useNotification} from '../../../Services/useNotification';
import {CreateDeliveryForm} from '../models/distribution.model';
import {DistributionFactory} from '../store/distributionFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

/**
 * Unified hook for distribution management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useDistributionManagement = () => {
  // Data stores
  const distributionStore = useDistributionStore();
  const uiStore = useDistributionUIStore();

  // Services
  const {success: showSuccess} = useNotification();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  // Use cases
  const getDistributionHistoryUseCase = useMemo(() =>
    DistributionFactory.createGetDistributionHistoryUseCase(), []
  );
  const getMarketersForDistributionUseCase = useMemo(() =>
    DistributionFactory.createGetMarketersForDistributionUseCase(), []
  );
  const getProductsForDistributionUseCase = useMemo(() =>
    DistributionFactory.createGetProductsForDistributionUseCase(), []
  );
  const getAvailableSerialsUseCase = useMemo(() =>
    DistributionFactory.createGetAvailableSerialsUseCase(), []
  );
  const createDeliveryUseCase = useMemo(() =>
    DistributionFactory.createCreateDeliveryUseCase(), []
  );
  const deleteTransactionUseCase = useMemo(() =>
    DistributionFactory.createDeleteTransactionUseCase(), []
  );

  // Destructure from distributionStore
  const {
    distributionHistory, marketers, products, availableSerials, productAvailableSerials,
    isLoading, error, selectedRecord, selectedProductId, pagination,
    setDistributionHistory, setMarketers, setProducts, setAvailableSerials, setProductAvailableSerials,
    setLoading, setError, clearError, setSelectedRecord, setSelectedProductId, setPagination,
    removeTransaction, pendingDeliveries, completedDeliveries, filteredRecords, resetStore
  } = distributionStore;

  // Destructure from uiStore
  const {
    activeTab, setActiveTab,
    isDeliveryModalOpen, openDeliveryModal, closeDeliveryModal,
    deliveryFormData, deliveryFormErrors, isSubmittingDelivery, selectedSerials, bulkSerials,
    setDeliveryFormData, setDeliveryFormErrors, setIsSubmittingDelivery, setSelectedSerials, setBulkSerials, resetDeliveryForm,
    isSearchingMarketers, isSearchingProducts, setIsSearchingMarketers, setIsSearchingProducts,
    isDetailsModalOpen, openDetailsModal, closeDetailsModal,
    showFilters, setShowFilters,
    reset: resetUI
  } = uiStore;

  // Track current request to prevent duplicates
  const currentRequestRef = useRef<string | null>(null);

  // Fetch distribution history - Define this first
  const fetchDistributionHistory = useCallback(async (page?: number, limit?: number) => {
    const currentPage = page || pagination?.page || 1;
    const currentLimit = limit || pagination?.limit || 20;
    const requestKey = `${currentPage}-${currentLimit}`;

    // Prevent duplicate requests
    if (currentRequestRef.current === requestKey) {
      return;
    }

    currentRequestRef.current = requestKey;

    try {
      setLoading(true);
      const result = await getDistributionHistoryUseCase.execute(undefined, currentPage, currentLimit);
      setDistributionHistory(result.items);
      setPagination(result.pagination);
      return true;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setDistributionHistory([]);
      return false;
    } finally {
      setLoading(false);
      currentRequestRef.current = null;
    }
  }, [getDistributionHistoryUseCase, setDistributionHistory, setPagination, setError, setLoading, errorHandlingService, pagination]);

  // Unified Actions
  const handleTabChange = useCallback((tab: 'history' | 'stats') => {
    setActiveTab(tab);
  }, [setActiveTab]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      await fetchDistributionHistory(page, pagination?.limit || 20);
    } catch (error) {
      // Error is handled in fetchDistributionHistory
    }

    if (pagination) {
      setPagination({...pagination, page});
    }
  }, [pagination, setPagination, fetchDistributionHistory]);

  const handlePageSizeChange = useCallback(async (pageSize: number) => {
    if (pagination) {
      setPagination({...pagination, limit: pageSize, page: 1});
      // Fetch new data with the new page size using the original function
      await fetchDistributionHistory(1, pageSize);
    }
  }, [pagination, setPagination, fetchDistributionHistory]);

  // Data fetching functions
  const fetchMarketers = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      const marketersData = await getMarketersForDistributionUseCase.execute(search);
      setMarketers(marketersData);
      return true;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setMarketers([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getMarketersForDistributionUseCase, setMarketers, setError, setLoading, errorHandlingService]);

  const fetchProducts = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      const productsData = await getProductsForDistributionUseCase.execute(search);
      setProducts(productsData);
      return true;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setProducts([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getProductsForDistributionUseCase, setProducts, setError, setLoading, errorHandlingService]);

  const fetchAvailableSerialsForProduct = useCallback(async (productId: string) => {
    try {
      setLoading(true);
      const serialsData = await getAvailableSerialsUseCase.execute(productId);
      setProductAvailableSerials(serialsData);
      return true;
    } catch (error: unknown) {
      const appError = errorHandlingService.toAppError(error);
      setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      setProductAvailableSerials([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAvailableSerialsUseCase, setProductAvailableSerials, setError, setLoading, errorHandlingService]);

  const clearProductAvailableSerials = useCallback(() => {
    setProductAvailableSerials([]);
  }, [setProductAvailableSerials]);


  // Initial data fetch on mount - Only run once
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          fetchMarketers(),
          fetchProducts(),
          fetchDistributionHistory()
        ]);
      } catch (error) {
        // Each fetch function handles its own errors, but catch here to prevent unhandled promise rejection
        // Errors are already logged and displayed by individual fetch functions
      }
    };

    loadInitialData();
  }, []); // Empty dependency array to run only once

  const handleDeliveryFormSubmit = useCallback(async (formData: CreateDeliveryForm) => {
    setIsSubmittingDelivery(true);
    try {
      const result = await createDeliveryUseCase.execute(formData);
      showSuccess(result.message || 'تم إنشاء التوزيع بنجاح');

      // Refresh the distribution history to show the new record
      try {
        await fetchDistributionHistory();
      } catch (refreshError) {
        // Silently ignore refresh errors - delivery was already created successfully
        // Refresh is non-critical for the create delivery operation
      }

      closeDeliveryModal();
      return true;
    } catch (error: unknown) {
      // Error notification is handled automatically by StandardizedErrorHandler
      errorHandlingService.handleError(error); // For notifications/logging
      return false;
    } finally {
      setIsSubmittingDelivery(false);
    }
  }, [setIsSubmittingDelivery, createDeliveryUseCase, showSuccess, closeDeliveryModal, fetchDistributionHistory, errorHandlingService]);

  const handleViewDetails = useCallback((record: any) => {
    openDetailsModal(record);
  }, [openDetailsModal]);

  const handleCloseDetailsModal = useCallback(() => {
    closeDetailsModal();
  }, [closeDetailsModal]);

  const handleDeleteTransaction = useCallback(async (transactionId: string) => {
    try {
      await deleteTransactionUseCase.execute(transactionId);
      removeTransaction(transactionId);
      showSuccess('تم حذف المعاملة بنجاح');
      return true;
    } catch (error: unknown) {
      // Error notification is handled automatically by StandardizedErrorHandler
      errorHandlingService.handleError(error); // For notifications/logging
      return false;
    }
  }, [deleteTransactionUseCase, removeTransaction, showSuccess, errorHandlingService]);

  const handleSerialToggle = useCallback((serial: string) => {
    const newSerials = selectedSerials.includes(serial)
      ? selectedSerials.filter((s: string) => s !== serial)
      : [...selectedSerials, serial];
    setSelectedSerials(newSerials);
  }, [selectedSerials, setSelectedSerials]);

  const handleBulkSerialsChange = useCallback((serials: string | string[]) => {
    // Handle both string (from input) and string[] (from component)
    const serialsArray = Array.isArray(serials)
      ? serials
      : serials.split('\n').filter(s => s.trim());
    setBulkSerials(serialsArray);
    // Also update selected serials
    setSelectedSerials(serialsArray);
  }, [setBulkSerials, setSelectedSerials]);

  const handleInputChange = useCallback((field: keyof CreateDeliveryForm, value: any) => {
    setDeliveryFormData({[field]: value});
    // Clear error when user starts typing
    if (deliveryFormErrors[field]) {
      setDeliveryFormErrors({...deliveryFormErrors, [field]: ''});
    }
  }, [setDeliveryFormData, deliveryFormErrors, setDeliveryFormErrors]);

  const handleFormSubmit = useCallback(async () => {
    try {
      const success = await handleDeliveryFormSubmit(deliveryFormData);
      if (success) {
        resetDeliveryForm();
      }
      return success;
    } catch (error) {
      // Error is already handled by handleDeliveryFormSubmit
      // This catch prevents unhandled promise rejection
      return false;
    }
  }, [handleDeliveryFormSubmit, deliveryFormData, resetDeliveryForm]);

  const handleFormReset = useCallback(() => {
    resetDeliveryForm();
  }, [resetDeliveryForm]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    resetStore();
    resetUI();
  }, [resetStore, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    isDeliveryModalOpen, openDeliveryModal, closeDeliveryModal,
    deliveryFormData, deliveryFormErrors, isSubmittingDelivery, selectedSerials, bulkSerials,
    isSearchingMarketers, isSearchingProducts,
    isDetailsModalOpen, selectedRecord,
    showFilters, setShowFilters,

    // Data
    distributionHistory, marketers, products, availableSerials, productAvailableSerials,
    pendingDeliveries, completedDeliveries, filteredRecords,
    selectedProductId, pagination,

    // Loading States
    isLoading,

    // Error States
    error,

    // Actions
    handleTabChange, handlePageChange, handlePageSizeChange, handleDeliveryFormSubmit, handleViewDetails, handleCloseDetailsModal,
    handleDeleteTransaction, handleSerialToggle, handleBulkSerialsChange,
    handleInputChange, handleFormSubmit, handleFormReset,
    setSelectedProductId, clearError, resetAllStores,

    // Data Fetching
    fetchMarketers, fetchProducts, fetchAvailableSerialsForProduct, fetchDistributionHistory, clearProductAvailableSerials,

    // Form Actions
    setDeliveryFormData, setDeliveryFormErrors, setIsSubmittingDelivery,
    setSelectedSerials, setBulkSerials, resetDeliveryForm,

    // Search Actions
    setIsSearchingMarketers, setIsSearchingProducts,

    // Modal Actions
    openDetailsModal, closeDetailsModal
  };
};
